import React from "react";
import { connect } from "react-redux";
import { Button } from "@vitalsource/vstui";
import { t, Translation } from "../../../../core/translate";
import { Popover } from "@vitalsource/vstui/labs";
import styled, { withTheme } from "styled-components";
import { Search as SearchIcon, XSmall } from "@vitalsource/vstui-icons";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import PopoverContent from "./popover-content";
import { OffScreenComponent } from "../../../../components/mixins";
import { ninja, cloud } from "@vitalsource/vstui/colors";
import {
  styledMedia,
  withAllMatches
} from "../../../../components/mediaquery/breakpoints";
import { parseSearchString } from "./utils";
import MobileContent from "./popover-content";
import { actions as libraryActions } from "../../../../../redux/ui/library";
import {
  actions as routerActions,
  selectors as routerSelectors
} from "../../../../../redux/router";

const FIELD_ID = "header-search-input";

const SearchForm = styled.form`
  position: relative;
  margin: 14px 0;
  z-index: 1;
  ${styledMedia.small`
    align-content: center;
    align-items: center;
    background: #1B1B26;
    display: flex;
    height: 44px;
    justify-content: center;
    margin:  0;
    position: relative;
    z-index: 1;
  `}
`;

const StyledSearchIcon = styled(SearchIcon)`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.vstui.color.white};
  ${styledMedia.small`
    left: 28%;
  `}
`;

const StyledInput = styled.input`
  display: block;
  width: 268px;
  margin: 0;
  font-size: 1.4rem;
  font-weight: 300;
  color: ${props => props.theme.vstui.color.white};
  background-color: ${ninja.getShade(10)};
  /* stylelint-disable property-no-vendor-prefix */
  -webkit-appearance: none !important;

  &::-ms-clear {
    display: none;
  }

  &::placeholder {
    color: ${props => props.theme.vstui.color.white};
  }

  *::-moz-focus-inner {
    border: 0;
  }

  && {
    border: 1px solid ${ninja.getShade(9)};
    border-radius: 100px;
    height: 30px;
    line-height: 30px;
    padding: 0 38px;
    margin-right: 5px;

    &:focus {
      box-shadow: 0 0 0 2px ${ninja.getShade(11)},
        0 0 0 4px rgba(255, 255, 255, 0.8);
      border-color: transparent;
    }
  }

  &:focus,
  &:not([value=""]) {
    color: ${ninja.getShade(11)};
    background-color: ${props => props.theme.vstui.color.white};
    &::placeholder {
      color: ${cloud.getShade(6)};
    }

    & + ${StyledSearchIcon} {
      color: ${props => props.theme.color.primary.getShade(6)};
    }
  }

  ${styledMedia.medium`
    width: 184px;
  `}
  ${styledMedia.small`
    width: 50%;
  `}
`;

const ClearContainer = styled.div`
  position: absolute;
  right: 1px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  ${styledMedia.small`
    right: 25%;
  `}

  svg {
    fill: ${props => props.theme.color.primary.getShade(6)};
  }
`;

const CloseSearch = styled(Button)`
  position: absolute;
  right: 10px;
  top: 5px;
  z-index: 1;
`;

const ClearButton = createThemedComponent(Button, {
  Button_borderRadius: "100px"
});

class Search extends React.Component {
  state = {
    searchTerm: "",
    focused: false
  };

  returnTo = "/";

  termChanged = e => {
    this.setState({
      searchTerm: e.currentTarget.value
    });
  };

  clearTerm = () => {
    this.setState({
      searchTerm: ""
    });
    this.input.focus();
  };

  goToSearchPage = e => {
    const { currentRoute, closePanel, push } = this.props;
    e.preventDefault();
    const route = currentRoute.hash.slice(1);
    if (route !== "/search") {
      this.returnTo = route;
    }
    const { term } = parseSearchString(this.state.searchTerm);
    const encodedTerm = encodeURIComponent(term);

    if (term) {
      push("search", {
        q: encodedTerm,
        redirectOnClose: this.returnTo
      });
      closePanel();
    }
  };

  // No timeout causes flickering in firefox
  handleFocus = () => {
    clearTimeout(this.timeoutId);
    this.setState({ focused: true });
  };

  handleBlur = () => {
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      this.setState({ focused: false });
    }, 0);
  };

  renderSearchForm = () => {
    return (
      <SearchForm onSubmit={this.goToSearchPage}>
        <OffScreenComponent>
          <label htmlFor={FIELD_ID}>
            <Translation id="base.search" />
          </label>
        </OffScreenComponent>
        <StyledInput
          id={FIELD_ID}
          type="search"
          placeholder={t("base.search")}
          value={this.state.searchTerm}
          onChange={this.termChanged}
          autoComplete="off"
          innerRef={el => (this.input = el)}
        />
        <StyledSearchIcon />
        {this.state.searchTerm && (
          <ClearContainer>
            <ClearButton
              type="reset"
              label={t("input.clear")}
              size="small"
              onClick={this.clearTerm}
              iconStart={<XSmall />}
              color="white"
              colorBehindButton="#FFFFFF"
            />
          </ClearContainer>
        )}
      </SearchForm>
    );
  };

  render() {
    const { term, type } = parseSearchString(this.state.searchTerm);
    if (this.props.isSmall) {
      return (
        <div>
          {this.renderSearchForm()}
          <CloseSearch
            naked
            onClick={this.props.closePanel}
            label={t("base.close")}
            iconStart={<XSmall />}
            color="white"
          />
          <MobileContent searchTerm={term} searchType={type} />
        </div>
      );
    }
    return (
      <Popover
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        open={this.state.focused}
        content={<PopoverContent searchTerm={term} searchType={type} />}
      >
        {this.renderSearchForm()}
      </Popover>
    );
  }
}

const mapStateToProps = state => ({
  currentRoute: routerSelectors.getCurrentRoute(state)
});

const mapDispatchToProps = dispatch => ({
  closePanel: data => dispatch(libraryActions.closePanel(data)),
  push: (dest, query) => dispatch(routerActions.push(dest, query))
});

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(withAllMatches(Search))
);
